import type { CityConfig } from "@/pages/city-template/types";

export const tempeConfig: CityConfig = {
  city: "Tempe",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "tempe",
  route: "/cities/tempe",
  title: "Emergency Passport Tempe, AZ — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Tempe, AZ — as fast as 24 hours. Fast Passport Center couriers to the Los Angeles Passport Agency in Tucson, about 110 miles south. A+ BBB rated. Serving Arizona State University students, faculty, and Phoenix East Valley residents.",
  h1: "Emergency & Expedited Passport Services in Tempe, AZ",
  heroSubtitle: "Tempe is home to Arizona State University and sits in the heart of the Phoenix East Valley, about 110 miles from the Los Angeles Passport Agency in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're an ASU student heading abroad for a semester, a tech professional at the Tempe Town Lake innovation corridor, or an East Valley family with international travel plans.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Tempe%20Arizona%20skyline%20at%20golden%20hour%20with%20Tempe%20Town%20Lake%20reflecting%20the%20downtown%20buildings%20and%20ASU%20campus%2C%20Hayden%20Butte%20and%20palm%20trees%20silhouetted%20against%20dramatic%20sunset%20sky%2C%20modern%20urban%20desert%20landscape%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20composition%2C%20warm%20golden%20light%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=tempe-hero-001&orientation=landscape",
  heroImageAlt: "Tempe Arizona skyline and Tempe Town Lake at golden sunset hour",
  geoPosition: "33.4255;-111.9400",
  geoPlacename: "Tempe, Arizona",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 110 miles — about 1 hour 45 minutes via I-10. The nearest passport agency to Tempe and the Phoenix East Valley.",
  },
  nearbyCitiesZips: [
    { name: "Downtown Tempe / ASU", zips: "85281, 85287" },
    { name: "South Tempe", zips: "85282, 85284" },
    { name: "Tempe Marketplace Area", zips: "85281" },
    { name: "Mesa", zips: "85201, 85202, 85204, 85210" },
    { name: "Chandler", zips: "85224, 85225, 85226" },
    { name: "Scottsdale", zips: "85251, 85257" },
    { name: "Guadalupe", zips: "85283" },
    { name: "Ahwatukee", zips: "85044, 85045" },
    { name: "Gilbert", zips: "85233, 85234" },
    { name: "Phoenix (South)", zips: "85040, 85042" },
    { name: "Sun Lakes", zips: "85248" },
    { name: "Queen Creek", zips: "85140, 85142" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 110 miles from Tempe" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "ASU & East Valley Hub", desc: "Serving Tempe, ASU community, Mesa, Chandler, and the Phoenix East Valley" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Tempe case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Expedited passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Urgent passport in Mesa AZ", route: "/cities/mesa" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Expedited passport in Chandler AZ", route: "/cities/chandler" },
    { name: "Fast passport in Gilbert AZ", route: "/cities/gilbert" },
    { name: "Emergency passport in Tucson AZ", route: "/cities/tucson" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Tempe remotely via secure courier. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. The nearest regional agency is the Western Passport Center in Tucson, approximately 110 miles south via I-10. Tempe sits in the Phoenix metro area, making overnight document shipping fast and reliable from the East Valley.",
  fpcOfficeDistance: "Serving all of the Phoenix East Valley",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Tempe, the ASU community, and the Phoenix East Valley. From Sun Devils studying abroad to tech professionals at Tempe Town Lake, we help East Valley travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Tempe and nearby communities including Mesa, Chandler, Scottsdale, Gilbert, Guadalupe, Ahwatukee, and the entire Phoenix East Valley region.",
  howItWorksIntro: "Most Tempe travelers complete everything online and ship documents overnight. At about 110 miles from Tucson, Fast Passport Center provides fast courier turnaround for East Valley residents — ideal for ASU students with study abroad deadlines and tech professionals with international business travel.",
  mistakesIntro: "Tempe travelers, especially ASU students studying abroad and young professionals with international career opportunities, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Tempe and East Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Tempe? Here are the most common ones we hear from ASU and East Valley travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "ASU study abroad deadlines — we get it done on time",
        "Perfect for East Valley tech professionals",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 110 miles to Tucson (under 2-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Tempe is about 110 miles from the Western Passport Center in Tucson — under 2 hours via I-10. Our couriers make rush delivery possible, as fast as 24 hours.",
      bullets: ["East Valley coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Tempe adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Tempe and East Valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Tempe and the East Valley.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "ASU student support expertise"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Tempe, ASU, and East Valley residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in the Tempe area.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Tempe and East Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Tempe AZ", description: "Urgent passport processing for Tempe, AZ travelers via Western Passport Center courier — about 110 miles from Tempe." },
    { name: "Passport Renewal Tempe AZ", description: "Fast passport renewal for Tempe, ASU community, and East Valley, Arizona residents." },
    { name: "New Passport Tempe AZ", description: "First-time passport application assistance for Tempe, ASU students, and East Valley residents." },
    { name: "Lost Passport Tempe AZ", description: "Passport replacement for lost or stolen passports for Tempe and East Valley residents." },
    { name: "Child Passport Tempe AZ", description: "Minor passport services for Tempe and East Valley families." },
    { name: "Name Change Passport Tempe AZ", description: "Passport name change service for Tempe, AZ residents." },
  ],
  testimonials: [
    {
      name: "Jordan K.",
      location: "ASU Senior, Tempe",
      rating: 5,
      text: "Semester abroad in Barcelona starting in 2 weeks. Realized my passport was expired during finals week. Fast Passport Center processed everything during crunch time — new passport in 4 days. Made my flight and didn't miss a single day of the program.",
      service: "Study Abroad Expedited",
    },
    {
      name: "Priya S.",
      location: "ASU Graduate Student",
      rating: 5,
      text: "Research conference in Tokyo with 6 days notice. My passport was valid but damaged. Fast Passport Center got me a replacement in 48 hours. Presented my research and made incredible connections. Lifesaving service.",
      service: "Emergency Replacement",
    },
    {
      name: "Marcus T.",
      location: "Tech Professional, Tempe Town Lake",
      rating: 5,
      text: "Startup got acquired — needed to be in London for integration meetings in 5 days. My passport was in a moving box somewhere. Fast Passport Center got me an emergency replacement in 36 hours. Closed the deal.",
      service: "Emergency Passport",
    },
    {
      name: "The Chen Family",
      location: "South Tempe",
      rating: 5,
      text: "Family trip to Taiwan for Lunar New Year. Three kids needed first passports. Fast Passport Center walked us through the DS-11 at the Tempe post office and had all five passports back in 7 days. Amazing service for a busy family.",
      service: "Family Passports",
    },
  ],
  facilities: [
    {
      id: "tmp-1",
      name: "Tempe Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 S Mill Ave",
      city: "Tempe",
      state: "AZ",
      zip: "85281",
      phone: "(480) 894-2617",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Downtown Tempe near ASU",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Tempe. Convenient for ASU students and downtown residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "tmp-2",
      name: "ASU Passport Acceptance Office",
      type: "University Passport Acceptance Facility",
      address: "1120 S Cady Mall, Memorial Union",
      city: "Tempe",
      state: "AZ",
      zip: "85287",
      phone: "(480) 965-3511",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM (academic year)",
      distance: "ASU Tempe Campus — Memorial Union",
      website: "https://www.asu.edu/",
      websiteLabel: "ASU Official Site",
      notes: "Convenient on-campus passport acceptance for ASU students, faculty, and staff. Check hours during summer and breaks.",
      icon: "ri-graduation-cap-line",
    },
    {
      id: "tmp-3",
      name: "Mesa Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "135 N Center St",
      city: "Mesa",
      state: "AZ",
      zip: "85201",
      phone: "(480) 969-3654",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "5 miles east of Tempe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Mesa and eastern Tempe residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "tmp-4",
      name: "Chandler Post Office — Downtown",
      type: "USPS Passport Acceptance Facility",
      address: "101 N Colorado St",
      city: "Chandler",
      state: "AZ",
      zip: "85225",
      phone: "(480) 963-3602",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "10 miles southeast of Tempe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Chandler and South Tempe residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "tmp-5",
      name: "Scottsdale Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1776 N Scottsdale Rd",
      city: "Scottsdale",
      state: "AZ",
      zip: "85257",
      phone: "(480) 946-9944",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "6 miles north of Tempe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Scottsdale and North Tempe residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "tmp-6",
      name: "Gilbert Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "137 E Elliot Rd",
      city: "Gilbert",
      state: "AZ",
      zip: "85234",
      phone: "(480) 892-9744",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "10 miles southeast of Tempe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Gilbert and southeastern Tempe residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — call NPIC, limited slots. No walk-ins accepted.", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 110-mile drive to Western Passport Center in Tucson via I-10", fpc: "Ship documents overnight from Tempe — no drive needed" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Tempe-Specific Guidance", agency: "Generic — no ASU, East Valley, or Sky Harbor expertise", fpc: "Full Tempe/ASU/East Valley/Sky Harbor knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking the Drive to Tucson Is Quick and Easy",
      description: "At 110 miles, Tempe is relatively close to the Western Passport Center — under 2 hours each way. But you still need a pre-scheduled appointment. Walk-ins are turned away. Don't waste 4 hours driving round trip when we can handle the courier delivery.",
    },
    {
      icon: "ri-graduation-cap-line",
      title: "ASU Students — Waiting Until the Last Week",
      description: "Every semester, we hear from ASU students who realize their passport is expired the week before their study abroad program starts. Standard processing takes 8-11 weeks. Start your passport application at least 8 weeks before your program departure date.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. The ASU campus area has multiple photo options, but always verify your photos meet the exact requirements. Student ID photos use completely different specifications.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Form",
      description: "ASU students applying for their first passport need DS-11 (in-person at acceptance facility). Students renewing an adult passport need DS-82 (mail-in). Mixing these up is the most common mistake we see from the ASU community.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel Ready",
      description: "Emergency processing requires confirmed travel documents. Have your study abroad acceptance letter, flight itinerary, or conference registration ready before reaching out.",
    },
    {
      icon: "ri-parent-line",
      title: "Out-of-State Students and Birth Certificates",
      description: "ASU attracts students from all 50 states. If you're from another state, make sure you have your certified birth certificate accessible — not at your parents' house back home. A photocopy is not acceptable.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Tempe, Mesa, Chandler, or Scottsdale." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Los Angeles Passport Agency in Tucson — about 110 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Los Angeles Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Tempe or East Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Tempe from a passport agency?",
      a: "Tempe is approximately 110 miles from the Los Angeles Passport Agency in Tucson — about 1 hour 45 minutes via I-10. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Can ASU students get fast passports for study abroad?",
      a: "Yes. ASU students are some of our most frequent customers in the East Valley. We understand study abroad deadlines and can process student passports in 2–9 business days with standard expedited service, or 24–48 hours for emergency processing. Start early — ideally 8 weeks before your program departure.",
    },
    {
      q: "How fast can I get an emergency passport in Tempe?",
      a: "Emergency processing is typically 24–48 hours for Tempe residents. Our couriers deliver directly to the Los Angeles Passport Agency in Tucson, about 110 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Tempe have its own passport agency?",
      a: "No. The closest regional passport agency is the Los Angeles Passport Agency in Tucson, about 110 miles from Tempe. Tempe has multiple acceptance facilities including the Tempe Main Post Office on Mill Avenue and the ASU Memorial Union passport office for students, faculty, and staff.",
    },
    {
      q: "Can I use the ASU passport office?",
      a: "Yes. The ASU Passport Acceptance Office in the Memorial Union on the Tempe campus accepts passport applications from ASU students, faculty, and staff. This is an acceptance facility (not a passport agency) — they accept applications but do not process passports. After your application is accepted, Fast Passport Center handles the expedited processing.",
    },
    {
      q: "Where do Tempe travelers fly internationally?",
      a: "Tempe travelers fly internationally through Phoenix Sky Harbor International Airport (PHX), just 5 miles west of Tempe. PHX offers extensive domestic and international routes. A valid U.S. passport is required for all international flights from PHX.",
    },
    {
      q: "How do I renew my passport in Tempe?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Tempe?",
      a: "CVS Pharmacy and Walgreens on Mill Avenue and Rural Road in Tempe offer passport photo services. The ASU Memorial Union may also offer photo services. The Tempe Main Post Office accepts applications but may not offer photos. Always verify your photos meet the exact 2x2 inch specification.",
    },
    {
      q: "What documents do I need for a first-time passport in Tempe?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Tempe Main Post Office, ASU Memorial Union, or nearby Mesa Post Office.",
    },
    {
      q: "Can you serve the entire East Valley?",
      a: "Yes. We serve all of the Phoenix East Valley including Tempe, Mesa, Chandler, Gilbert, Scottsdale, Ahwatukee, Guadalupe, Sun Lakes, and Queen Creek. Documents ship overnight from any UPS location in the East Valley, and we deliver your passport back to your address.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Tempe (Mill Ave)", address: "901 S Mill Ave, Tempe, AZ 85281", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Tempe (Rural Rd)", address: "725 S Rural Rd, Tempe, AZ 85281", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Tempe", address: "930 E University Dr, Tempe, AZ 85281", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Tempe (Southern Ave)", address: "1850 E Southern Ave, Tempe, AZ 85282", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Mesa (Dobson Rd)", address: "1951 W Main St, Mesa, AZ 85201", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Tempe Marketplace", address: "2000 E Rio Salado Pkwy, Ste 109, Tempe, AZ 85281", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Tempe", address: "1810 E Baseline Rd, Tempe, AZ 85283", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Tempe and South East Valley residents. Closest MVD to ASU.", featured: true },
    { name: "AZ MVD — Mesa", address: "4123 E Valley Auto Dr, Mesa, AZ 85206", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Mesa and eastern East Valley residents. Full-service MVD location.", featured: false },
    { name: "AZ MVD — Scottsdale", address: "7339 E Paradise Ln, Scottsdale, AZ 85260", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Scottsdale and North Tempe residents.", featured: false },
  ],
  airports: [
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "5 miles west of Tempe",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport — just 5 miles from downtown Tempe. Extensive domestic and international routes via all major carriers. About 10 minutes via Loop 202. Passport required for all international flights.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: true,
    },
    {
      code: "AZA",
      name: "Phoenix-Mesa Gateway Airport",
      distance: "25 miles southeast of Tempe",
      address: "6033 S Sossaman Rd, Mesa, AZ 85212",
      note: "East Valley's secondary airport with Allegiant and Sun Country service to domestic destinations. About 30 minutes southeast via Loop 202 and US-60.",
      flightUrl: "https://www.gatewayairport.com/",
      flightLabel: "AZA Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "110 miles south of Tempe",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona's international hub. About 1 hour 45 minutes south via I-10. Co-located with the Los Angeles Passport Agency.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};